import { Injectable, inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, CanActivateFn } from '@angular/router';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
class AuthGuardService {
  
  constructor(private router: Router, private authService: AuthService) {}
  
  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> | boolean {
    const url = state.url;

    // early return true for 'login' route
    if (url === '/login') {
      return true;
    }

    return new Promise<boolean>((resolve) => {
      this.authService.provider.isAuthenticated().then(authenticated => {
        // early return false if not authenticated
        if (!authenticated) {
          resolve(false);
          // start the authentication process
          this.authService.startAuthentication();
          return;
        }

        // allow access to certain routes for all authenticated users (without checking permissions)
        // Note: this is necessary b/c AlertRoutingModule requires 'hickory.IncidentManagement' for the '/alerts'
        // route or else it auto-redirects to '/dashboard'
        const lowerUrl = url.toLowerCase();
        if (
          lowerUrl.startsWith('/alerts/received') || // ROAD-5151
          lowerUrl.startsWith('/alerts?userdataid=') // CH-2113 - see json-storage component
        ) {
          // ensure that we have a valid user object before we proceed
          this.authService.getCurrentUser().then(user => {
            resolve(!!user);
          });
        } else {
          // get permissions and redirect url from the route.data (if present)
          const permissions = next.data['permissions'] || [];
          let redirectUrl = next.data['redirectUrl'];
          const alwaysAllowRegion0 = next.data['alwaysAllowRegion0'];
          
          // perform the permission check and return the result
          this.authService.checkPermissions(permissions).then(permitted => {
            const region0Permitted = alwaysAllowRegion0 && this.authService.isAdminUser();
            const isPermitted = permitted || region0Permitted;

            resolve(isPermitted);
  
            // special handling for receive-only users - ROAD-5151, ROAD-6352
            if (!permitted && lowerUrl === '/alerts' && !this.authService.isAdminUser()) {
              redirectUrl = '/alerts/received';
            }

            // if the permission check failed and no redirectUrl is specified, navigate to the home page
            // without this, the user would still be allowed to access the route
            if((permissions as string[])?.length > 0 && !isPermitted && !redirectUrl) {
              redirectUrl = '/';
            }

            // if the permission check failed, navigate to the redirect url
            if (!permitted && redirectUrl) {
              this.router.navigate([redirectUrl]);
            }
          });
        }
      });
    });
  }
}
  
export const AuthGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> | boolean => {
  return inject(AuthGuardService).canActivate(next, state);
}